// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-11-27 01:16:58 +0100 (Thu, 27 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158697 $
//  Purpose   : column definitions
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon, Button} from 'semantic-ui-react'
import {isElement, isFragment} from 'react-is'

// ---Framework imports
import {
  get,
  dict,
  sum,
  mapItems,
  uniq,
  strip,
  isArray,
  isString,
  flatten,
  keys,
  values,
  isFunction,
  merge,
  isNil,
  range,
} from 'ldash'

import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {ucFirst} from 'uix/utils'
import {Link} from 'uix/components'
import {checkAcl} from 'uix/auth/acl'
import {STATE_NAME2VALUE} from 'uix/states/constants'

// ---Local imports
import {ColumnTypes, ColumnTypeNames as $, nodeAccessor, ColHeader} from './columnTypes'
import styles from './styles.scss'

// ---Globals

const KNOWN_STATES = [
  [
    'release',
    'release (freigabe) state indicates if the record public or still only internally available',
  ],
  ['qs', 'qs (quality assurance) state indicates if the record fullfills all quality requirements'],
  [
    'wf',
    `cataloging state indicates if the record is synchronized correctly
      with a remote bibliographic catalogue`,
  ],
  [
    'ar',
    `access restriction state defines areas (networks) that have full
      access to the resources (pdfs, scans,fulltexts) of the record`,
  ],
  [
    'segmentizer',
    `segmentizer state tracks the workflow
  from start states to end states of the newspaper segmentization (issue construction)`,
  ],
  ['zipcapsule', ``],
  ['metadata', ``],
  ['license', `indicates copyright license`],
  ['srusync', ``],
  ['gndsync', ``],
  ['zmods_validation', ``],
  ['ocrstate', ``], // XXX: is called `ocr` in OPR_Status, alias do to namespace collision (another 'ocr' ns exists)
  ['ner', ``],
  ['pdfcutter', ``],
  ['apipush_obvidn', ``],
  ['apipush_obvurn', ``],
  ['apipush_datacite', ``],
]
const KNOWN_STATES_TYPES = KNOWN_STATES.map(e => e[0])

export const getColumnDescriptionText = (colcfg, excludeCols = []) => {
  /* generate textual representation of all columns (in markdown style)
   */

  let active_ns = [
    null,
    'parents',
    'desccount',
    'childcount',
    'oc',
    'urn',
    'doi',
    'accstats',
    // states
    ...KNOWN_STATES_TYPES,
  ]
  let ignore_cols = ['expander', 'dicon', 'otidlink', ...excludeCols]
  let cols = flatten(
    active_ns.map(ns => {
      let cols = []
      if (ns) {
        let ns_doc = column_descriptions[`ns.${ns}`]
        cols = [...cols, `- namespace ${ns}: ${ns_doc || ''}`]
      } else {
        cols = [...cols, `- general properties`]
      }
      //~ if (ns_doc) {
      //~ }
      let ns_cols = mapItems(
        colcfg,
        (cid, c) => c,
        (cid, c) => c.namespace == ns && !ignore_cols.includes(cid)
      )
      let col_docs = ns_cols.map(c => {
        let choices = ''
        if (c.categorialLabels) {
          // CategorialColumn
          // the term "value-choices" is also referenced in the table-context gpt instructions (so dont change)
          choices = `. Available value-choices:\n    - ${values(c.categorialLabels).join(
            '\n    - '
          )}`
        }
        return `  - ${c.id} (${c.coltype}): ${c.docs || ''}${choices}`
      })
      return [...cols, ...col_docs]
    })
  )
  return `comma separated list of record properties:\n${cols.join('\n')}. `
}

let state_description = stateTypes =>
  dict(
    flatten(
      stateTypes.map(([st, ns_desc]) => [
        [`ns.${st}`, ns_desc],
        [
          `${st}.value`,
          `state value Available value-choices:\n    - ${keys(STATE_NAME2VALUE[st]).join(
            '\n    - '
          )}`,
        ],
      ])
    )
  )

// will be added as colcfg.docs
const column_descriptions = {
  domain: 'primary collection membership (Sammlung), can be thematical/configurative',
  dname: 'internal short name of domain',
  dtype: 'type of domain',
  otid: 'record id, also called vlid',
  type: 'record type (one of the possible selected_document_types)',
  caption: 'short record description',
  tcaption: 'short record description (with type icon)',
  insertts: 'record insert timestamp',
  updatets: 'record update timestamp',
  delstate: 'record deletion state',
  // parent
  'ns.parent': 'tree parent record',
  'parent.id': 'id of parent record',
  'parent.type': 'type of parent record',
  'parent.caption': 'caption of parent record',
  // descendants
  'ns.desccount':
    'Count children/descendants (tree relation) records of different types. These are slow (but cached for 7 days)',
  'desccount.metadata': 'children/descendants metadata/title records',
  'desccount.substructure': 'children/descendants structure (table of contents)',
  'desccount.page': 'children/descendants page records of book structure',
  //children
  'ns.childcount': 'Count direct child relation records of different types ',
  'childcount.metadata': 'children of type metadata/title',
  'childcount.substructure': 'children of type substructure (table of contents)',
  'childcount.page': 'children of type page',
  // opr_content
  'ns.oc': 'various title related fields (from database table OPR_Content)',
  'oc.releasedate': 'first time/date the record was made public/online (freigabe)',
  'oc.sourceident': 'identifier in remote system, catalog number',
  'oc.year': 'original publishing/creation date (year granularity)',
  'oc.date': 'original publishing/creation date (day granularity)',
  'oc.number':
    'MODS: part/detail/number = bibliographic numbering in periodical/multivolume contexts',
  'oc.comment': '',
  'oc.pagecount':
    'number of pages extracted from bibliograhic metadata, needs not to be the same as the count in oc.pagecache',
  'oc.pagecache':
    'effective count/number of pages in database (children/descendant records) (prefer this count)',
  'oc.mpmode':
    'Meta-Parent-Mode indicates if there are other document/metadata typed nodes above/below in tree structure',
  'oc.xmlorigin': 'metadata origin/source of this record',
  'oc.email': '',
  'oc.signature': 'bibliographic media number',
  'oc.textstate': 'indicator if fulltext/ocr output is available for pages',
  'oc.restrictionstate': 'indicates restriction state of page or substructure (TOC) children',
  'oc.owner': 'freeform string of owning library, see dconfig.vlm.data.oc_owner',

  // urn
  'ns.urn': 'uniform resource number related information',
  'urn.ident': 'uniform resource number',
  'urn.insertts': 'insertion/creation date',
  // dois
  'ns.doi': 'doi related information',
  'doi.ident': 'digital object identifier',
  'doi.insertts': 'insertion/creation date',
  // access statistics
  'ns.accstats': 'web frontend access statistics',
  'accstats.visits': 'count visitors / web frontents users',
  'accstats.downloads': 'count resource downloads',
  'ns.accstats': 'web frontend access statistics',
  ...state_description(KNOWN_STATES),
}

// all registered columns, not exported - replaced with register* funcs
let Columns = [
  [$.DomainColumn, 'domain'],
  [$.DomainTypeColumn, 'dtype'],
  [$.DomainColumn, 'dicon', {renderLabel: () => null, Header: () => null, width: 20, hidden: true}],

  [$.DomainNameColumn, 'dname'],

  [$.TypeColumn, 'type'],
  [$.TypeCaptionColumn, 'tcaption'],
  [$.LinkColumn, 'caption', {needsColData: false}],
  [$.IdColumn, 'otid'],
  [$.DateColumn, 'insertts', {groupBySupport: true}],
  [$.BooleanColumn, 'delstate', {groupBySupport: true, trueVal: 1}],
  [
    $.LinkColumn,
    'otidlink',
    {
      needsColData: false,
      accessor: node => node.otid,
      serializeCSV: value => env.apiurl.baseUrl(`/${value}`),
    },
  ],

  // Parent tree row
  [$.RelatedIdColumn, 'parent.id', {groupBySupport: true}],
  [
    $.TypeColumn,
    'parent.type',
    {groupBySupport: true, fields: ['parent.rcv_datatype', 'parent.rcv_type']},
  ],
  [$.LinkColumn, 'parent.caption'],

  // descendant counts
  // these are slow (but cached for 7 days)
  [$.NumberColumn, 'desccount.page'],
  [$.NumberColumn, 'desccount.metadata'],
  [$.NumberColumn, 'desccount.substructure'],

  // child counts
  ...['page', 'substructure', 'metadata'].map(n => [
    $.NumberColumn,
    `childcount.${n}`,
    {transform: v => v ?? 0},
  ]),

  // Datatype 1 (metadata)
  [$.TextColumn, 'oc.sourceident', {i18nKey: 'IDN', copyButton: true}],
  [
    $.TextColumn,
    'oc.email',
    {
      i18nKey: 'Mail',
      copyButton: false,
      translate: v =>
        v ? (
          <>
            <Icon name="mail outline"></Icon>
            <tt>{v}</tt>
          </>
        ) : null,
    },
  ],
  [$.TextColumn, 'oc.comment'],

  [$.TextColumn, 'oc.orgform'],
  [$.TextColumn, 'oc.webinfo1'],
  // in use?
  //~ [$.TextColumn, 'oc.bibcomment'],

  [$.TextColumn, 'oc.signature', {i18nKey: 'signature', copyButton: true}],
  [
    $.TextColumn,
    'oc.oaiident',
    {
      i18nKey: 'oaisource',
      copyButton: true,
      translate: v => (
        <Link classname="import" tabname="oai" query={{verb: 'GetRecord', identifier: v}}>
          {v}
        </Link>
      ),
    },
  ],
  [$.DateColumn, 'oc.oaits'],
  [$.DateColumn, 'oaiharvest.datestamp'],
  [$.BooleanColumn, 'oaiharvest.outdated'],
  [
    $.CategorialColumn,
    'oaiharvest.state',
    {labels: {new: 'Neu', updated: 'Aktualisiert', orphaned: 'Verwaist', deleted: 'Gelöscht'}},
  ],
  // create script says, its probably obsolete
  //~ [$.TextColumn, 'oc.medianumber', {copyButton: true}],
  [
    $.TextColumn,
    'oc.physicallocation',
    {i18nKey: 'mods_locationPhysicalLocation', copyButton: true},
  ],
  [$.TextColumn, 'oc.owner', {copyButton: true}],
  [$.TextColumn, 'oc.restrictionstate'],

  [
    $.CategorialColumn,
    'oc.textstate',
    {i18nKey: 'Textstate', labels: {0: 'Kein', 1: 'OCR', 2: 'Wiki', 3: 'OCR/Wiki'}},
  ],
  [$.DateColumn, 'oc.releasedate', {i18nKey: 'index_state.release.ts'}],
  [$.DateColumn, 'oc.sc_deliverydate', {i18nKey: 'deliverydatecount'}],

  [$.NumberColumn, 'oc.maxaccesscount'],
  [$.NumberColumn, 'oc.pagecache', {i18nKey: 'Seiten'}],
  [$.NumberColumn, 'oc.pagecount', {i18nKey: 'Seiten (MODS)'}],
  [$.DateColumn, 'oc.year', {field: 'oc.date', format: 'yyyy'}],
  [$.DateColumn, 'oc.endyear', {format: 'yyyy'}],
  [$.DateColumn, 'oc.date', {format: 'ISO_DATE'}],

  [$.PageImageColumn, 'oc.ot_titlepage', {i18nKey: 'Titelseite'}],
  [$.BooleanColumn, 'oc.titlepagemode', {i18nKey: 'Titelseite Manuell', trueVal: 1}],
  [$.BooleanColumn, 'oc.generateidentifier', {i18nKey: 'Gen-URN', trueVal: 1}],

  [$.NumberColumn, 'oc.countnames'],
  [$.NumberColumn, 'oc.countplaces'],
  [$.NumberColumn, 'oc.counttopics'],
  [
    $.CategorialColumn,
    'oc.mpmode',
    {
      labels: {
        0: 'Ist elternlos und ist kinderlos',
        1: 'Hat Eltern und ist kinderlos',
        2: 'Hat Kinder und ist elternlos',
        3: 'Hat Eltern und Kinder',
      },
    },
  ],
  [
    $.CategorialColumn,
    'oc.xmlorigin',
    {
      labels: ({i18n}) =>
        dict(
          range(20)
            .filter(i => i18n[`oc_xmlorigin_${i}`])
            .map(i => [i, i18n[`oc_xmlorigin_${i}`]])
        ),
    },
  ],

  // identifier
  ...flatten(
    ['urn', 'doi'].map(n => [
      [$.TextColumn, n + '.ident', {i18nKey: n, copyButton: true}],
      [$.DateColumn, n + '.updatets', {}],
      [$.DateColumn, n + '.insertts', {}],
    ])
  ),
  // pagebox
  //~ ...flatten(
  //~ ['pbox_ocr', 'pbox_tpage'].map(n => [
  //~ [$.NumberColumn, n + '.left'],
  //~ [$.NumberColumn, n + '.top'],
  //~ [$.NumberColumn, n + '.right'],
  //~ [$.NumberColumn, n + '.bottom'],
  //~ [$.NumberColumn, n + '.rel_ot_id'],
  //~ [$.NumberColumn, n + '.ot_id'],
  //~ [$.TextColumn, n + '.title'],
  //~ [$.DateColumn, n + '.updatets', {}],
  //~ [$.DateColumn, n + '.insertts', {}],
  //~ ])
  //~ ),
  // states
  ...flatten(
    KNOWN_STATES_TYPES.map(n => [
      [
        $.StateColumn,
        n + '.value',
        {i18nKey: `${n}_state_tree`, statusType: n != 'ocrstate' ? n : 'ocr'},
      ],
      [$.DateColumn, n + '.changets', {}],
      [$.DateColumn, n + '.updatets', {}], // could be used for tracking changes on comment (reenable in ufields.py)
      //~ [$.DateColumn, n + '.insertts', {}],
      [$.UserColumn, n + '.pu_id', {i18nKey: 'Geändert von'}],
      [$.TextColumn, n + '.comment', {i18nKey: 'Kommentar'}],
    ])
  ),

  // derived restriction date
  [$.StateColumn, 'restriction.value', {statusType: 'ar'}],
  [$.BooleanColumn, 'restriction.inherited'],
  [$.RelatedIdColumn, 'restriction.inherited_otid'],

  // local/manual capsules
  [$.NumberColumn, 'capsule_more.lcapsule_count'],
  [$.FileSizeColumn, 'capsule_more.lcapsule_size'],
  [$.DateColumn, 'capsule_more.lcapsule_date'],

  // Datatype 10 (domain)
  [$.DateColumn, 'od.configupdatets'],
  [$.DateColumn, 'od.cssupdatets'],
  [$.DomainStateColumn, 'od.state'],
  [$.TextColumn, 'od.shortname'],
  [$.TextColumn, 'od.name'],
  [$.TextColumn, 'od.webname'],
  [$.TextColumn, 'od.routingprefix'],

  // Datatype 3 (page)
  [$.MimetypeColumn, 'op.mimetype', {i18nKey: 'Dateityp', groupBySupport: true}],
  [$.TextColumn, 'op.bpp', {i18nKey: 'BPP'}],
  [$.NumberColumn, 'op.imgwidth', {i18nKey: 'Breite'}],
  [$.NumberColumn, 'op.imgheight', {i18nKey: 'Höhe'}],
  [$.FileSizeColumn, 'op.filesize', {i18nKey: 'Dateigröße'}],
  [$.FileSizeColumn, 'op.orgfilesize', {i18nKey: 'Dateigröße (org)'}],
  [$.DateColumn, 'op.imgdatetime', {i18nKey: 'Scandatum'}],
  [$.TextColumn, 'op.imgpagename', {i18nKey: 'Seitenname'}],
  [$.DateColumn, 'op.modificationtimestamp', {i18nKey: 'Dateidatum'}],
  [$.DateColumn, 'op.insertts'],
  [$.DateColumn, 'op.updatets'],
  [$.TextColumn, 'op.paginationcaption', {i18nKey: 'Paginierung'}],
  [$.ResourceLinkColumn, `op.url_wc0`, {field: 'op.id', restype: 'wc0'}],
  [$.ResourceLinkColumn, `op.url_archive`, {field: 'op.id', restype: 'archive'}],

  [$.PageImageColumn, 'pageimage', {accessor: 'otid', needsColData: false, header: '', width: 20}],
  [
    $.MultiFieldColumn,
    'op.dimensions',
    {
      fields: ['op.imgwidth', 'op.imgheight'],
      Cell: ({value}) => (value.op_imgwidth ? `${value.op_imgwidth}x${value.op_imgheight}` : null),
    },
  ],
  [
    $.MultiFieldColumn,
    'op.dimensions_dpi',
    {
      fields: ['op.imgwidth', 'op.imgheight', 'oip.dpix'],
      Cell: ({value}) =>
        value.op_imgwidth ? `${value.op_imgwidth}x${value.op_imgheight} (${value.oip_dpix})` : null,
    },
  ],
  [
    $.TextColumn,
    'op.paginationtype',
    {
      i18nKey: 'Pag/Typ',
      translate: (v, i18n) => (v ? i18n.get(`pagination_${v}`) : null),
      groupBySupport: true,
    },
  ],

  [
    $.CategorialColumn,
    'op.state',
    {
      i18nKey: 'Status',
      labels: ({i18n}) =>
        dict(
          range(20)
            .filter(i => i18n[`op_state_${i}`])
            .map(i => [i, i18n[`op_state_${i}`]])
        ),
    },
  ],
  [
    $.TextColumn,
    'op.archivecfg',
    {
      i18nKey: 'Store',
      translate: v => `Archiv ${v}`,
      groupBySupport: true,
    },
  ],
  [$.BooleanColumn, 'op.iccstate', {i18nKey: 'ICC', trueVal: 1}],
  [$.TextColumn, 'op.hashsum', {i18nKey: 'Hash'}],

  [$.TextColumn, 'op.ident', {i18nKey: 'Identifier'}],
  [$.TextColumn, 'op.webcache', {i18nKey: 'WC'}],
  [$.TextColumn, 'op.comment', {i18nKey: 'Kommentar'}],
  [$.NumberColumn, 'op.custom'],
  [$.FileSizeColumn, 'op.webcachesize', {i18nKey: 'WC-Größe'}],

  [$.StateColumn, 'pageqa', {i18nKey: 'pageqa_state_tree', statusType: 'pageqa'}],

  // Image-Profile
  [$.TextColumn, 'oip.dpix', {i18nKey: 'DPI', groupBySupport: true, uniquify: true}],
  [$.TextColumn, 'oip.dpiy', {i18nKey: 'DPIY', groupBySupport: true, uniquify: true}],
  [
    $.MultiFieldColumn,
    'oip.dpixy',
    {
      fields: ['oip.dpix', 'oip.dpiy'],
      i18nKey: 'DPI (X/Y)',
      Cell: ({value}) => (value.oip_dpix ? `${value.oip_dpix}x${value.oip_dpiy}` : null),
    },
  ],

  // Tiff Tags
  [$.TextColumn, 'oip.software', {i18nKey: 'Software', groupBySupport: true}],
  [$.TextColumn, 'oip.camera', {i18nKey: 'Kamera', groupBySupport: true}],
  [$.TextColumn, 'oip.creator', {i18nKey: 'Creator', groupBySupport: true}],

  [
    $.TextColumn,
    'oip.oic_id',

    {
      i18nKey: 'ICC-ID',
      translate: v => (v ? `Profil ${v}` : null),
      groupBySupport: true,
    },
  ],
  // Datatype 0 (substructure)
  [$.TextColumn, 'structid.ident'],
  // Datatype = 13 (name)
  [$.TextColumn, 'on.ident', {i18nKey: 'GND'}],
  [$.TextColumn, 'on.localident', {i18nKey: 'Local-ID'}],
  [$.TextColumn, 'on.wdident', {i18nKey: 'Wikidata'}],
  [$.TextColumn, 'on.name', {i18nKey: 'Name'}],
  [$.DateColumn, 'on.date', {format: 'yyyy'}],
  [$.CategorialColumn, 'on.manualrelease', {labels: {0: 'Auto', 1: 'Job', 2: 'Manuell'}}],

  // Datatype = 16 (place)
  [$.TextColumn, 'opp.ident', {i18nKey: 'GND'}],
  [$.TextColumn, 'opp.geoident', {i18nKey: 'Geonames'}],
  [$.TextColumn, 'opp.wdident', {i18nKey: 'Wikidata'}],
  [$.NumberColumn, 'opp.lon', {i18nKey: 'Lat'}],
  [$.NumberColumn, 'opp.lat', {i18nKey: 'Lon'}],

  // Datatype = 19 (subject)
  [$.TextColumn, 'oss.ident', {i18nKey: 'GND'}],
  // Datatype = 24 (subject)
  [$.TextColumn, 'og.ident'],
  [$.BooleanColumn, 'og.enabled'],

  // Resources
  ...flatten(
    [
      'pdf',
      'fpk_plain',
      'fpk_alto',
      'fpk_html',
      'webres',
      'genres',
      'zip',
      'epub',
      'nerres',
      'tei',
      'html',
      'mhtml',
      'mp3res',
      'mp4res',
      'musicxmlres',
      'isores',
      'textres',
    ].map(n => [
      [$.FileSizeColumn, `${n}.filesize`],
      [$.DateColumn, `${n}.insertts`],
      [$.DateColumn, `${n}.modificationtimestamp`],
      [$.BooleanColumn, `${n}.avail`, {field: `${n}.id`, mapper: val => Boolean(val)}],
      [$.ResourceLinkColumn, `${n}.url`, {field: `${n}.id`, restype: n}],
      [
        $.ResourceColumn,
        `resource.${n}`,
        // ResourceColumn needs these fields (sadly not declarable *in* ResourceColumn)
        {
          fields: [`${n}.id`, `${n}.filesize`, `${n}.modificationtimestamp`, `${n}.filename`],
          restype: n,
        },
      ],
    ])
  ),
  [$.TextColumn, 'pdf.filename'],
  //~ [$.TextColumn, 'webres.filename'],
  [$.MimetypeTextColumn, `webres.mimetype`, {i18nKey: 'Format', groupBySupport: true}],

  // Text Resources on page datatypes
  ...flatten(
    ['ocr', 'fr', 'alto', 'ocrp', 'ocrg'].map(n => [
      [$.FileSizeColumn, `${n}.filesize`],
      [$.DateColumn, `${n}.insertts`],
      [$.DateColumn, `${n}.modificationtimestamp`],
      [$.BooleanColumn, `${n}.compression`],
      [$.FileSizeColumn, `${n}.cachesize`],
      [$.BooleanColumn, `${n}.avail`, {field: `${n}.id`, mapper: e => Boolean(e)}],
      [$.ResourceLinkColumn, `${n}.url`, {field: `${n}.id`, restype: n}],
      [$.BooleanColumn, `${n}.is_remote`, {field: `${n}.url`, mapper: e => e.startsWith('http')}],
      [
        $.ResourceColumn,
        `resource.${n}`,
        // ResourceColumn needs these fields (sadly not declarable *in* ResourceColumn)
        {
          fields: [`${n}.id`, `${n}.filesize`, `${n}.modificationtimestamp`, `${n}.filename`],
          restype: n,
        },
      ],
    ])
  ),
  [
    $.TextColumn,
    'ocr.rcv_type',
    {
      i18nKey: 'OCR-Format',
      groupBySupport: true,
      translate: v =>
        v
          ? {2: 'FineReader', 6: 'Alto', 20: 'PAGE', 21: 'Google-V', 23: 'HOCR', 24: 'Microsoft'}[
              v
            ] ?? v
          : null,
    },
  ],

  // custom Relations
  [$.MetadataRelationColumn, 'host.*'],
  [$.TypeColumn, 'host.type', {fields: ['host.rcv_type']}],
  [$.MetadataRelationColumn, 'metaparent.*'],
  [$.TypeColumn, 'metaparent.type', {fields: ['metaparent.rcv_type']}],

  // Wiki
  [$.TextColumn, 'ow.ident', {i18nKey: 'Identifier'}],
  [$.TextColumn, 'ow.text', {i18nKey: 'Text'}],
  [$.TextColumn, 'ow.meta', {i18nKey: 'Meta'}],
  [
    $.TextColumn,
    'ow.state',
    {i18nKey: 'Status', translate: (v, i18n) => (v ? i18n.get(`wiki_state_${v}`) : null)},
  ],
  [$.MimetypeTextColumn, 'ow.mimetype', {i18nKey: 'Format'}],
  [$.DateColumn, 'ow.insertts'],
  [$.DateColumn, 'ow.updatets'],

  // Transcriptions
  [$.UserColumn, 'transcript.last_op_puid', {i18nKey: 'Geändert von'}],
  [$.DateColumn, 'transcript.last_op_ts', {i18nKey: 'Geändert am'}],
  [$.UserColumn, 'transcript.checkpoint_puids', {i18nKey: 'Autor (Geändert)'}],
  [$.BooleanColumn, 'transcript.release_op', {i18nKey: 'Freigabe', mapper: val => Boolean(val)}],
  [$.DateColumn, 'transcript.release_op_ts', {i18nKey: 'Freigabe (Datum)'}],
  [$.UserColumn, 'transcript.release_op_puid', {i18nKey: 'Freigabe (Autor)'}],

  [$.NumberColumn, 'accstats.visits'],
  [$.NumberColumn, 'accstats.downloads'],
]

export const unregisterColumns = cols => {
  // either list of colids, or list of full column spec tripel (type, colid, options)
  let colids = isString(cols[0]) ? new Set(cols) : new Set(cols.map(col => col[1]))
  Columns = Columns.filter(col => !colids.has(col[1]))
  for (let colid of colids) {
    delete ColumnsById[colid]
  }
}

/* register new colums given by triples ['NumberColumn', colid/fieldname, options]
 */
export const registerColumns = cols =>
  _registerColumns(cols.map(([coltype, colid, options]) => [$[coltype], colid, options]))

const _registerColumns = new_cols => {
  /* register new colums
   */
  let indexed_cols = indexColumsById(new_cols)
  // replace global Columns
  Columns = [...Columns.filter(c => !indexed_cols[c[1]]), ...new_cols]
  // update inplace (as it's exported)
  merge(ColumnsById, indexed_cols)
}

const indexColumsById = cols =>
  dict(
    cols.map(cfg => {
      if (!isArray(cfg)) {
        throw Error(`invalid column spec ${cfg}`)
      }
      let [name, colid, options = {}] = cfg
      return [colid, {name, coltype: ColumnTypes[name], options}]
    })
  )

export const ColumnsById = indexColumsById(Columns)

const EXPANDER_STYLE = {
  cursor: 'pointer',
  fontSize: 15,
  color: 'gray',
  paddingTop: '5px',
  textAlign: 'center',
  userSelect: 'none',
}

export const configureColumns = env => {
  /* connect globally registered columns with environment
   */
  env = env ?? {}
  let buildGo = data => {
    if (env.buildLink) {
      return {id: data.otid, odid: data.odid, ...env.buildLink(data)}
    } else {
      return {id: data.otid, odid: data.odid}
    }
  }

  env = {...env, buildGo}

  let chosenHeaders = null
  if (env.chosenColumns && env.unchosableColumns) {
    chosenHeaders = (colhead, i18nKey, HeaderButtons) => ({column, data}) => {
      let content = column.id
      if (isString(colhead) || isElement(colhead) || isFragment(colhead)) {
        content = colhead
      } else if (isFunction(colhead)) {
        content = colhead()
      } else if (colhead === undefined) {
        content = <ColHeader i18nKey={i18nKey} colid={column.id} />
      } else if (colhead !== null) {
        log.warn('invalid header type %o', colhead)
      }

      let onClick = event => {
        if (env.onUnSelect) {
          env.onUnSelect(column.id)
        }
        event.stopPropagation()
      }
      return (
        <>
          {HeaderButtons}
          {!env.unchosableColumns.includes(column.id) && (
            <Button
              style={{padding: '0.2em', zIndex: 100}}
              basic
              compact
              color="grey"
              size="tiny"
              icon="close"
              floated="right"
              onClick={onClick}
            />
          )}
          <span>{content}</span>
        </>
      )
    }
  }

  let res = Columns.map(cfg => {
    let [name, colid, options = {}] = cfg
    let coltype = ColumnTypes[name]

    if (isString(options)) {
      options = {i18nKey: options}
    }
    if (options.acl && !checkAcl(options.acl, env.user, env.dname)) {
      return null
    }

    let namespace = colid.includes('.') ? colid.split('.')[0] : null

    let colcfg = coltype(env, colid, options)

    let {aggregate, Cell, Header: colhead, AggregatedFooter, HeaderButtons} = colcfg

    if (options.header !== undefined) {
      // full overide via options
      // see registerSimpleTextColumns()
      colhead = options.header
    }

    if (colcfg.filterable === true) {
      log.error('only filterable=false allowed: %o', colid)
    }

    colcfg = {
      accessor: nodeAccessor(colid),
      id: colid,
      coltype: name,
      namespace,
      hidden: !!options.hidden,
      docs: column_descriptions[colid],
      ...colcfg,
    }

    if (chosenHeaders) {
      colcfg.Header = chosenHeaders(colhead, options.i18nKey, HeaderButtons)
    }
    if (aggregate && Cell) {
      // detect filtered subRows case (by counting)
      // and adapt aggregation
      colcfg = {
        ...colcfg,
        aggregate: (vals, rows) => [aggregate(vals, rows), vals.length],
        Cell: ({value, aggregated, subRows, ...props}) => {
          if (aggregated) {
            let [aggregatedValue, numAggregated] = value
            if (subRows.length < numAggregated) {
              // apply subRows filtering
              value = aggregate(subRows.map(r => r[colid]))
            } else {
              value = aggregatedValue
            }
          }
          return Cell({value, aggregated, subRows, ...props})
        },
      }
    }

    if (aggregate && AggregatedFooter) {
      colcfg = {
        ...colcfg,
        Footer: ({data, column}) =>
          AggregatedFooter(
            aggregate(
              data.map(e => {
                let value = e[column.id]
                if (e._aggregated) {
                  let [aggregatedValue, numAggregated] = value
                  if (e._subRows.length < numAggregated) {
                    // apply subRows filtering
                    value = aggregate(e._subRows.map(r => r[column.id]))
                  } else {
                    value = aggregatedValue
                  }
                }
                return value
              }),
              data, // react-table signature compat
              // indicate if value list is already a aggregate result
              Boolean(data[0]?._aggregated)
            )
          ),
      }
    }

    let _Cell = colcfg.Cell
    colcfg.Cell = ({value, ...props}) => {
      if (_Cell) {
        return _Cell({value, ...props})
      } else {
        return String(value)
      }
    }
    // patched rt-6, show record description for subrows in grouped column
    colcfg.PivotSubCell = ({original}) => (
      <Link lazy {...buildGo(original)}>
        <span style={{color: '#AAA'}}>{original.caption}</span>
      </Link>
    )

    return colcfg
  })
  // maybe columns removed due to aclCheck
  res = res.filter(Boolean)

  return [
    ...res,
    {
      id: 'expander',
      expander: true,
      Header: null,
      width: 30,
      Expander: ({isExpanded, original, ...rest}) => (
        <Icon fitted name={isExpanded ? 'minus square outline' : 'plus square outline'} />
      ),
      style: EXPANDER_STYLE,
      needsColData: false,
    },
  ]
}

export const getNoDataColumns = columnConfig =>
  new Set(columnConfig.filter(col => col.needsColData === false).map(col => col.id))

export const getHiddenColumns = columnConfig =>
  new Set(columnConfig.filter(col => col.hidden).map(col => col.id))
